import { Can } from "@/casl-provider";
import { useFitSettingsGetQuery } from "@/common/api/fit-settings/use-fit-settings-get.query";
import { useFitSettingsUpdateMutation } from "@/common/api/fit-settings/use-fit-settings-update.mutation";
import { handleFormSubmission } from "@/common/helpers/form-submission";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyValue } from "@/components/ui/empty-value";
import { Label } from "@/components/ui/label";
import { TriangleAlertIcon } from "lucide-react";
import type { ReactElement } from "react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { BcaCompanySelector } from "./_components/bca-company-selector";
import { FitSettingsSkeleton } from "./_components/fit-settings-skeleton";

export function FitSettingsView(): ReactElement {
  const { t } = useTranslation(["fit", "common"]);
  const bcaCompanyId = useId();
  const { data: settings, isLoading } = useFitSettingsGetQuery();
  const { mutateAsync, isPending } = useFitSettingsUpdateMutation();

  const handleUpdateBcaCompany = async (uuid: string | null): Promise<void> => {
    if (uuid === (settings?.bcaCompany?.uuid ?? null)) {
      return;
    }

    await handleFormSubmission(() => mutateAsync({ bcaCompany: uuid }), {
      success: t("fit:FitSettingsSuccessUpdate"),
      error: t("fit:FitSettingsErrorUpdate"),
    });
  };

  const content = isLoading ? (
    <FitSettingsSkeleton />
  ) : settings ? (
    <Card>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 space-y-1">
            <Label className="text-sm font-medium" htmlFor={bcaCompanyId}>
              {t("fit:BcaCompany")}
            </Label>
            <p className="text-muted-foreground text-xs">{t("fit:BcaCompanyDescription")}</p>
          </div>
          <Can I="update" a="FitSettings">
            <BcaCompanySelector
              id={bcaCompanyId}
              value={settings.bcaCompany?.uuid}
              onValueChange={(uuid) => void handleUpdateBcaCompany(uuid)}
              disabled={isPending}
              className="w-full max-w-sm shrink-0"
            />
          </Can>
          <Can not I="update" a="FitSettings">
            <span className="shrink-0 text-sm">{settings.bcaCompany?.name || <EmptyValue />}</span>
          </Can>
        </div>
        <Can I="update" a="FitSettings">
          <Alert variant="warning">
            <TriangleAlertIcon />
            <AlertDescription>{t("fit:BcaCompanyChangeWarning")}</AlertDescription>
          </Alert>
        </Can>
      </CardContent>
    </Card>
  ) : null;

  return (
    <Can I="read" a="FitSettings">
      <CenteredLayout
        variant={CenteredLayoutVariant.Default}
        title={t("fit:FitSettings")}
        subTitle={t("fit:FitSettingsDescription")}
      >
        {content}
      </CenteredLayout>
    </Can>
  );
}
